import { Component, ViewChild, ElementRef, NgZone, Output, EventEmitter, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { Category, Coords, TechnicalServiceFilter, TechnicalService, SearchResult } from '../../interfaces';
import { MapsAPILoader, LatLngBounds, LatLng } from '@agm/core';
import { MapService, GeolocationService, TechnicalServiceService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { PlaceService } from 'src/app/services/place.service';

/**
 * Variable to use the Google API
 */
declare const google: any;
/**
 * Search panel to filter the technical services
 *
 * @export
 * @class SearchPanelComponent
 * @implements {OnInit}
 */
export abstract class SearchPanelComponent {
  /**
   * Search input reference
   *
   * @type {ElementRef}
   * @memberof SearchPanelComponent
   */
  @ViewChild('addressSearch', {
    read: false,
    static: true,
  })
  addressSearchRef: ElementRef;

  /**
   * Emit an event with all the results
   *
   * @type {EventEmitter<SearchResult[]>}
   * @memberof SearchPanelComponent
   */
  @Output() searchResult: EventEmitter<SearchResult> = new EventEmitter();

  /**
   * Search Form
   *
   * @type {FormGroup}
   * @memberof SearchPanelComponent
   */
  searchForm: FormGroup;

  /**
   * Flag to show a loading indicator when the user search for locations
   *
   * @type {boolean}
   * @memberof SearchPanelComponent
   */
  isSearching: boolean = false;

  /**
   * Flag to show a loading indicator when the browser gets the current geolocation
   *
   * @type {boolean}
   * @memberof SearchPanelComponent
   */
  isGeolocating: boolean = false;

  /**
   * Flag to show inputs of categories to search
   *
   * @type {boolean}
   * @memberof SearchPanelComponent
   */
  isSearchCategories: boolean = false;

  /**
   * Creates an instance of SearchPanelComponent.
   * @param {FormBuilder} fb
   * @param {NgZone} ngZone
   * @param {MapsAPILoader} mapApiLoader
   * @param {MapService} mapService
   * @param {GeolocationService} geolocationService
   * @param {TechnicalServiceService} technicalServiceService
   * @param {PlaceService} placeService
   * @memberof SearchPanelComponent
   */
  constructor(
    protected fb: FormBuilder,
    protected ngZone: NgZone,
    protected mapApiLoader: MapsAPILoader,
    protected mapService: MapService,
    protected geolocationService: GeolocationService,
    protected technicalServiceService: TechnicalServiceService,
    protected placeService: PlaceService
  ) {}

  /**
   * Search technical services
   *
   * @param {FormGroup} form
   * @memberof SearchPanelComponent
   */
  async search(form: FormGroup) {
    if (form.valid) {
      try {
        // Disable after getting values
        form.disable();
        this.isSearching = true;

        const searchFilter = this.setSearchFilter(form);

        // Do search
        await this.searchTechnicalServicesAndLocateInMap(searchFilter);
      } finally {
        this.isSearching = false;
        form.enable();
      }
    }
  }

  /**
   *
   */
  protected abstract setSearchFilter(form: FormGroup): TechnicalServiceFilter;

  /**
   * Calculate map bounds according to technical services coords
   *
   * @private
   * @param {TechnicalService[]} technicalServices
   * @param {Coords} coords
   * @memberof SearchPanelComponent
   */
  private fitToBounds(technicalServices: TechnicalService[], coords: Coords) {
    const LatLngList: Array<LatLng> = technicalServices.map(
      (technicalService: TechnicalService) =>
        new google.maps.LatLng(technicalService.coords.lat, technicalService.coords.lng),
    );

    // Add current coords to calculate bounds
    LatLngList.push(new google.maps.LatLng(coords.lat, coords.lng));

    //  Create a new viewpoint bound
    const bounds: LatLngBounds = new google.maps.LatLngBounds();

    //  Go through each...
    LatLngList.forEach((_, index: number) => bounds.extend(LatLngList[index]));

    //  Fit these bounds to the map
    this.mapService.fitBounds(bounds);
  }

  /**
   * Search technical services
   *
   * @private
   * @param {TechnicalServiceFilter} searchFilter
   * @returns {Promise<any>}
   * @memberof SearchPanelComponent
   */
  protected abstract async searchTechnicalServicesAndLocateInMap(searchFilter: TechnicalServiceFilter): Promise<any>;

  /**
   * Emit an event with the search result
   *
   * @private
   * @param {TechnicalService[]} technicalServices
   * @param {TechnicalServiceFilter} searchFilter
   * @memberof SearchPanelComponent
   */
  protected emitEvent(technicalServices: TechnicalService[], searchFilter: TechnicalServiceFilter) {
    this.searchResult.emit({
      technicalServices,
      category: searchFilter.category,
      subCategory: searchFilter.subCategory,
      elementFix: searchFilter.elementFix,
    });
  }

  /**
   * Sets Technical services in map and adjust map zoom acording to bounds and updates coords
   *
   * @private
   * @param {TechnicalService[]} technicalServices
   * @param {Coords} coords
   * @memberof SearchPanelComponent
   */
  protected updateData(technicalServices: TechnicalService[], coords: Coords) {
    this.mapService.updateCoords(coords);

    if (technicalServices.length) {
      // Zoom map to fit all markers
      this.fitToBounds(technicalServices, coords);
    } else {
      // Set default zoom
      this.mapService.updateZoom(environment.google.maps.zoom);
    }
  }

  /**
   * Set user position
   *
   * @private
   * @memberof MapComponent
   */
  async setCurrentPosition() {
    try {
      this.isGeolocating = true;

      const coords: Coords = await this.geolocationService.getCurrentLocation();
      const coordsStr: string = `${coords.lat},${coords.lng}`;

      // Set user coords on Location input
      this.searchForm.get('location').patchValue(coordsStr);

      // Set user coors on Coords (hidden) input
      this.setCoordsOnSearchForm(coords);
    } finally {
      this.isGeolocating = false;
    }
  }

  /**
   *
   * @param {*} _
   * @param {Category} category
   * @returns
   * @memberof SearchPanelComponent
   */
  trackById(_: any, category: Category) {
    return category.id;
  }

  /**
   * Bind input with Places API
   *
   * @private
   * @memberof SearchPanelComponent
   */
  protected async initAutocomplete() {
    await this.mapApiLoader.load();

    const autocomplete = new google.maps.places.Autocomplete(this.addressSearchRef.nativeElement, {
      types: ['geocode'],
    });

    autocomplete.addListener('place_changed', () => this.onPlaceChanged(autocomplete));
  }

  /**
   * When the place changed update the current coords
   *
   * @private
   * @param {*} autocomplete
   * @memberof SearchPanelComponent
   */
  protected onPlaceChanged(autocomplete: any) {
    this.ngZone.run(() => {
      // get place result
      // type: google.maps.places.PlaceResult

      const place: any = autocomplete.getPlace();
      // verify result
      if (place.geometry === undefined || place.geometry === null) {
        return;
      }
      
      const geocoder = new google.maps.Geocoder();
      const coords: Coords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
      geocoder
      .geocode({ location: coords })
      .then((response)=>{
        if(response.results[0]){
          try{
            let postalCode = response.results[0].address_components.find(function (component) {
              return component.types[0] == "postal_code";
          });
          postalCode = postalCode.long_name.replace(/\D/g,'');
          this.placeService.updatePostalCode(postalCode);
          console.log('nuevo codigo postal: ' + postalCode);
          }catch{
            console.log('Fallo proceso de respuesta geocode');
            this.placeService.updatePostalCode('');
          }
        }else{
          this.placeService.updatePostalCode('');
          console.log('No se encontraron datos de esta ubicacion')
        }
      })
      .catch((e)=>{
        console.log('Geocode fallo: ' + e);
      })
      this.setCoordsOnSearchForm(coords);
    });
  }

  /**
   * Sets new coords to search form
   *
   * @private
   * @param {Coords} coords
   * @memberof SearchPanelComponent
   */
  protected setCoordsOnSearchForm(coords: Coords) {
    const coordsControl: FormControl = this.searchForm.get('coords') as FormControl;
    coordsControl.patchValue(coords);
    coordsControl.updateValueAndValidity();
  }

  /**
   * Init form with specific validations
   *
   * @private
   * @memberof SearchPanelComponent
   */
  protected abstract createForm();
}
